import { useEffect, useMemo, useState, useRef } from "react";
import { ActionIcon, Button, Center, Flex, Loader, Text, Title, Tooltip, useMantineTheme } from "@mantine/core";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  type MRT_SortingState,
  type MRT_ColumnFiltersState,
} from "mantine-react-table";
import { useDebouncedValue } from "@mantine/hooks";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { errorNotification } from "@/shared/utils/errorNotification";
import {
  OrderStatusBadge,
  OrderTypeBadge,
  ORDER_STATUS_OPTIONS,
  ORDER_TYPE_OPTIONS,
} from "@/pages/admin/AdminStatusBadge";
import { formatINR, formatDate } from "@/shared/utils/format.utils";
import { IconEye } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import OrderSyncPasswordModal from "./OrderSyncPasswordModal";
import OrderSyncResultsModal from "./OrderSyncResultsModal";
import { ORDER_SYNC_PASSWORD } from "@/shared/constants/general.const";

type OrderListItem = {
  date: Date;
  orderId: string;
  userId: string | null;
  username: string;
  schemeName: string;
  amount: number | null;
  orderType: "P" | "R" | "S";
  orderStatus: string;
  rtaRemarks: string | null;
  mode: string | null;
  allotmentAmount: number | null;
  allotmentUnits: number | null;
  allotmentNav: number | null;
  allotmentFolio: string | null;
  allotmentStampDuty: number | null;
  allotmentDate: string | null;
  redemptionDate: string | null;
  redemptionUnits: number | null;
  redemptionAmount: number | null;
  redemptionNav: number | null;
  redemptionNavDate: string | null;
  redemptionFolio: string | null;
  redemptionTds: number | null;
  redemptionExitLoad: number | null;
  redemptionStt: number | null;
  redemptionPayoutDate: string | null;
  redemptionPayoutUtr: string | null;
};

const PAGE_SIZE = 100;

export default function OrdersPage() {
  const theme = useMantineTheme();
  const navigate = useNavigate();

  const [orders, setOrders] = useState<OrderListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: PAGE_SIZE });
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [debouncedFilters] = useDebouncedValue(columnFilters, 400);

  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [pendingSyncType, setPendingSyncType] = useState<"orders" | "sxp" | null>(null);
  const [syncLoading, setSyncLoading] = useState<"orders" | "sxp" | null>(null);
  const [resultsModalOpen, setResultsModalOpen] = useState(false);
  const [syncResults, setSyncResults] = useState<any>(null);

  const previousParamsRef = useRef<string>("");

  const filterParams = useMemo(() => {
    const active = debouncedFilters.filter((f) => f.value !== undefined && f.value !== null && f.value !== "");
    return Object.fromEntries(active.map((f) => [f.id, f.value]));
  }, [debouncedFilters]);

  useEffect(() => {
    const params = {
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      sorting: sorting.map((s) => ({ id: s.id, desc: s.desc })),
    };

    const paramsString = JSON.stringify({ ...params, filterParams, refreshKey });
    if (paramsString === previousParamsRef.current) return;
    previousParamsRef.current = paramsString;

    const fetchOrders = async () => {
      setLoading(true);
      try {
        const filter: Record<string, any> = {};
        if (filterParams.orderId) filter.orderId = filterParams.orderId;
        if (filterParams.username) filter.username = filterParams.username;
        if (filterParams.schemeName) filter.schemeName = filterParams.schemeName;
        if (filterParams.orderType) filter.orderType = filterParams.orderType;
        if (filterParams.orderStatus) filter.orderStatus = filterParams.orderStatus;

        const res = await trpc.orders.list.query({
          page: params.page,
          limit: params.limit,
          filter: Object.keys(filter).length > 0 ? filter : undefined,
          sorting: params.sorting.length > 0 ? params.sorting : undefined,
        });

        setOrders(res.data.orders as OrderListItem[]);
        setTotalRows(res.data.meta.total);
      } catch (err) {
        handleTrpcError(err);
        setOrders([]);
        setTotalRows(0);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [pagination.pageIndex, pagination.pageSize, filterParams, sorting, refreshKey]);

  // Reset to page 1 when filters change
  useEffect(() => {
    if (pagination.pageIndex !== 0) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [filterParams]);

  const columns: MRT_ColumnDef<OrderListItem>[] = useMemo(
    () => [
      {
        accessorKey: "date",
        header: "Date",
        size: 150,
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ cell }) => <Text fz="sm">{formatDate(cell.getValue<Date>())}</Text>,
      },
      {
        accessorKey: "orderId",
        header: "Order ID",
        size: 160,
        enableSorting: true,
        Cell: ({ cell }) => cell.getValue<string>() || "—",
      },
      {
        accessorKey: "username",
        header: "Username",
        size: 160,
        enableSorting: true,
        Cell: ({ cell }) => cell.getValue<string>() || "—",
      },
      {
        accessorKey: "schemeName",
        header: "Scheme Name",
        size: 220,
        enableSorting: true,
        Cell: ({ cell }) => cell.getValue<string>() || "—",
      },

      {
        accessorKey: "orderType",
        header: "Order type",
        size: 150,
        filterVariant: "select",
        mantineFilterSelectProps: {
          data: ORDER_TYPE_OPTIONS,
          clearable: true,
        },
        enableSorting: true,
        Cell: ({ cell }) => <OrderTypeBadge type={cell.getValue<string>()} />,
      },
      {
        accessorKey: "amount",
        header: "Amount",
        size: 140,
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ cell }) => <Text fz="sm">{formatINR(cell.getValue<number | null>())}</Text>,
      },
      {
        accessorKey: "orderStatus",
        header: "Order status",
        size: 190,
        filterVariant: "select",
        mantineFilterSelectProps: {
          data: ORDER_STATUS_OPTIONS,
          clearable: true,
        },
        enableSorting: true,
        Cell: ({ cell }) => <OrderStatusBadge status={cell.getValue<string>()} />,
      },
      {
        accessorKey: "rtaRemarks",
        header: "RTA Remarks",
        size: 260,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const v = cell.getValue<string | null>();
          return v ? (
            <Text
              fz="sm"
              title={v}
              style={{ maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              {v}
            </Text>
          ) : (
            "—"
          );
        },
      },
      {
        accessorKey: "allotmentAmount",
        header: "Allotment amount",
        size: 160,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell }) => <Text fz="sm">{formatINR(cell.getValue<number | null>())}</Text>,
      },
      {
        accessorKey: "allotmentUnits",
        header: "Allotment units",
        size: 160,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const v = cell.getValue<number | null>();
          return v == null ? "—" : v;
        },
      },
      {
        accessorKey: "allotmentNav",
        header: "Allotment NAV",
        size: 150,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const v = cell.getValue<number | null>();
          return v == null ? "—" : v;
        },
      },
      {
        accessorKey: "allotmentFolio",
        header: "Allotment folio",
        size: 170,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? "—",
      },
      {
        accessorKey: "allotmentStampDuty",
        header: "Allotment stamp duty",
        size: 190,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell }) => <Text fz="sm">{formatINR(cell.getValue<number | null>())}</Text>,
      },
      {
        accessorKey: "allotmentDate",
        header: "Allotment date",
        size: 160,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const v = cell.getValue<string | null>();
          return <Text fz="sm">{v ? formatDate(v) : "—"}</Text>;
        },
      },
      {
        accessorKey: "redemptionDate",
        header: "Redemption date",
        size: 160,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const v = cell.getValue<string | null>();
          return <Text fz="sm">{v ? formatDate(v) : "—"}</Text>;
        },
      },
      {
        accessorKey: "redemptionUnits",
        header: "Redemption units",
        size: 160,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const v = cell.getValue<number | null>();
          return v == null ? "—" : v;
        },
      },
      {
        accessorKey: "redemptionAmount",
        header: "Redemption amount",
        size: 170,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell }) => <Text fz="sm">{formatINR(cell.getValue<number | null>())}</Text>,
      },
      {
        accessorKey: "redemptionNav",
        header: "Redemption NAV",
        size: 150,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const v = cell.getValue<number | null>();
          return v == null ? "—" : v;
        },
      },
      {
        accessorKey: "redemptionNavDate",
        header: "Redemption NAV date",
        size: 180,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const v = cell.getValue<string | null>();
          return <Text fz="sm">{v ? formatDate(v) : "—"}</Text>;
        },
      },
      {
        accessorKey: "redemptionFolio",
        header: "Redemption folio",
        size: 170,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? "—",
      },
      {
        accessorKey: "redemptionTds",
        header: "Redemption TDS",
        size: 160,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell }) => <Text fz="sm">{formatINR(cell.getValue<number | null>())}</Text>,
      },
      {
        accessorKey: "redemptionExitLoad",
        header: "Redemption exit load",
        size: 180,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell }) => <Text fz="sm">{formatINR(cell.getValue<number | null>())}</Text>,
      },
      {
        accessorKey: "redemptionStt",
        header: "Redemption STT",
        size: 160,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell }) => <Text fz="sm">{formatINR(cell.getValue<number | null>())}</Text>,
      },
      {
        accessorKey: "redemptionPayoutDate",
        header: "Redemption payout date",
        size: 190,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const v = cell.getValue<string | null>();
          return <Text fz="sm">{v ? formatDate(v) : "—"}</Text>;
        },
      },
      {
        accessorKey: "redemptionPayoutUtr",
        header: "Redemption payout UTR",
        size: 190,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? "—",
      },
      // {
      //   accessorKey: "mode",
      //   header: "Mode",
      //   size: 130,
      //   enableSorting: false,
      //   enableColumnFilter: false,
      //   Cell: ({ cell }) => cell.getValue<string | null>() ?? "—",
      // },
    ],
    []
  );

  const runSync = async (type: "orders" | "sxp") => {
    if (syncLoading) return;
    setSyncLoading(type);
    try {
      const res = type === "orders" ? await trpc.orders.sync.mutate() : await trpc.orders.sxpSync.mutate();

      if (!res?.ok) {
        errorNotification({ title: "Sync Failed", message: "No success response received from server." });
        return;
      }

      // Store the full response data and open results modal
      setSyncResults({
        summary: res.summary ?? {},
        results: res.results ?? [],
        errors: res.errors ?? [],
      });
      setResultsModalOpen(true);
      setRefreshKey((k) => k + 1);
    } catch (err: any) {
      const code = err?.data?.code;
      if (code === "FORBIDDEN") {
        errorNotification({ title: "Not Authorized", message: "You don't have permission to run this action." });
      } else {
        handleTrpcError(err);
      }
    } finally {
      setSyncLoading(null);
    }
  };

  const requestSync = (type: "orders" | "sxp") => {
    if (syncLoading) return;

    if (!ORDER_SYNC_PASSWORD.trim()) {
      errorNotification({
        title: "Sync Disabled",
        message: "Frontend action password is not configured.",
      });
      return;
    }

    setPendingSyncType(type);
    setPasswordModalOpen(true);
  };

  if (loading && orders.length === 0) {
    return (
      <Center h="100vh">
        <Loader size="lg" type="dots" />
      </Center>
    );
  }

  return (
    <Flex direction="column" w="100%" p="xl">
      <Flex justify="space-between" align="center" mb="xl">
        <Title order={2} fw={700}>
          All Orders
        </Title>
        <Flex gap="sm">
          <Tooltip label="Syncs latest Lumpsum and SxP order statuses from BSE. Run this if a webhook missed any status updates.">
            <Button
              color="primary"
              variant="light"
              onClick={() => requestSync("orders")}
              loading={syncLoading === "orders"}
              disabled={!!syncLoading}
            >
              Order Sync
            </Button>
          </Tooltip>
          <Tooltip label="Syncs only latest SxP (SIP) order statuses from BSE. Run this if a webhook missed any status updates.">
            <Button
              color="primary"
              variant="light"
              onClick={() => requestSync("sxp")}
              loading={syncLoading === "sxp"}
              disabled={!!syncLoading}
            >
              SxP Sync
            </Button>
          </Tooltip>
        </Flex>
      </Flex>

      <OrderSyncPasswordModal
        opened={passwordModalOpen}
        onClose={() => {
          setPasswordModalOpen(false);
          setPendingSyncType(null);
        }}
        expectedPassword={ORDER_SYNC_PASSWORD}
        title={pendingSyncType === "sxp" ? "Authorize SxP Sync" : "Authorize Order Sync"}
        description="This is a sensitive admin action. Enter the shared password to continue."
        isLoading={!!syncLoading}
        onAuthorized={async () => {
          if (pendingSyncType) await runSync(pendingSyncType);
        }}
      />

      <OrderSyncResultsModal
        opened={resultsModalOpen}
        onClose={() => setResultsModalOpen(false)}
        syncType={pendingSyncType || "orders"}
        data={syncResults}
      />

      <MantineReactTable
        columns={columns}
        data={orders}
        enableRowNumbers
        enableStickyHeader
        enableColumnPinning
        enableRowActions
        enableColumnResizing
        columnResizeMode="onEnd"
        layoutMode="semantic"
        manualPagination
        manualFiltering
        manualSorting
        enableGlobalFilter={false}
        positionToolbarAlertBanner="none"
        rowCount={totalRows}
        onPaginationChange={setPagination}
        onColumnFiltersChange={setColumnFilters}
        onSortingChange={setSorting}
        state={{
          pagination,
          columnFilters,
          sorting,
          isLoading: loading,
        }}
        positionActionsColumn="last"
        mantinePaginationProps={{
          rowsPerPageOptions: ["10", "25", "50", "100"],
        }}
        paginationDisplayMode="pages"
        renderRowActions={({ row }) => (
          <Tooltip label={row.original.userId ? "View User" : "User not available"}>
            <ActionIcon
              c="slate.6"
              variant="transparent"
              disabled={!row.original.userId}
              onClick={() => row.original.userId && navigate(`/users/${row.original.userId}`)}
            >
              <IconEye size={18} />
            </ActionIcon>
          </Tooltip>
        )}
        initialState={{
          columnPinning: { right: ["mrt-row-actions"] },
          density: "xs",
          pagination: { pageIndex: 0, pageSize: PAGE_SIZE },
        }}
        mantineTableBodyCellProps={{
          style: {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            borderRight: "1px solid var(--mantine-color-gray-2)",
          },
        }}
        mantineTableHeadCellProps={{
          style: {
            fontWeight: 400,
            fontSize: "var(--mantine-font-size-sm)",
            color: theme.colors.slate[5],
            borderRight: "1px solid var(--mantine-color-gray-2)",
            backgroundColor: "var(--mantine-color-gray-0)",
          },
        }}
        mantineTableContainerProps={{
          style: {
            maxHeight: "calc(100vh - 250px)",
            minHeight: "100px",
            width: "100%",
            overflowX: "auto",
          },
        }}
        renderEmptyRowsFallback={() => (
          <tr>
            <td colSpan={columns.length + 1} style={{ textAlign: "center", padding: "2rem" }}>
              No orders found. Try adjusting your filters.
            </td>
          </tr>
        )}
      />
    </Flex>
  );
}
